import {Stack, Typography} from '@mui/material';
import React, {Fragment, useEffect} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Navigate, useLocation, useNavigate} from 'react-router-dom';

import operationalCostImg from 'presentation/assets/images/general-security.svg';
import {
	AlertInfo,
	AlertModal,
	CTAButtonFixed,
	MainHeader,
	PasswordInput,
	SubheaderWithSearch,
} from 'presentation/components';
import {HealthBookingEnum, HomeEnum, OperationStateTypesEnum} from 'presentation/constants';

import {useServicePermission} from 'presentation/hooks/useServicePermission';
import Icon from 'presentation/icons';
import {useCallChatRoom} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {Loading} from 'presentation/components/Loading/Loading';
import {ServicesTypesEnum} from 'presentation/pages/private/services/types';
import {useOperationalCostPassword} from 'presentation/hooks/useOperationalCostPassword';
import {StyledCard, StyledContainer, StyledImage, StyledLabel} from './styles';
import {SimpleConfirmationDrawer} from 'presentation/pages/private/booking/components/ConfirmationDrawer/SimpleConfirmationDrawer';

type IFormData = {
	operationalPass: string;
};

const {primaryButton, secondaryButton, subtitle, dontHavePass, description, passLabel, inputLabel} =
	translations['pt-br'].privateFlow.operationalCost.needPassword;
const {telehealthTitle, emergencyTitle, authorizationsTitle, healthTitle, examTitle, odontoTitle} =
	translations['pt-br'].privateFlow.operationalCost.headerTitles;
const {serverError, unexpectedError, serverErrorTitle, tryAgain} = translations['pt-br'].commons;

export function OperationalCostNeedPassword() {
	const navigate = useNavigate();
	const {state} = useLocation() as {state: {type: ServicesTypesEnum}};

	const {callChatRoom, IS_LOADING: callChatRoomIsLoading} = useCallChatRoom();
	const methods = useForm<IFormData>({
		defaultValues: {
			operationalPass: '',
		},
	});
	const {watch} = methods;

	const {
		setOperationStatePass,
		IS_SUBMITTING_PASS,
		IS_ERROR_PASS,
		passwordErrorMessage,
		setOperationStateUrl,
		IS_SUBMITTING_GET_URL,
		IS_ERROR_GET_URL,
	} = useServicePermission();
	const {
		handleVerifyOperationalCostPassword,
		openConfirmationDrawer,
		handleOpenConfirmationDrawer,
		handleCloseConfirmationDrawer,
	} = useOperationalCostPassword();
	const inputValue = watch('operationalPass');

	const IS_SUBMITTING = IS_SUBMITTING_PASS || IS_SUBMITTING_GET_URL;
	const IS_DISABLED = IS_SUBMITTING || inputValue.length < 6;

	function handleBack() {
		navigate(HomeEnum.HOME_PAGE);
	}

	async function handleSubmitPass(data: IFormData) {
		await handleVerifyOperationalCostPassword(data.operationalPass, state.type);
	}

	useEffect(() => {
		if (inputValue.length < 6) {
			setOperationStatePass(OperationStateTypesEnum.NONE);
		}
	}, [inputValue]);

	useEffect(() => {
		if (IS_ERROR_PASS) {
			methods.setError('operationalPass', {type: 'value', message: passwordErrorMessage});
		} else {
			methods.clearErrors();
			setOperationStatePass(OperationStateTypesEnum.NONE);
		}
	}, [IS_ERROR_PASS]);

	if (!state?.type) {
		return <Navigate to={HomeEnum.HOME_PAGE} />;
	}

	const headerTitle: Record<ServicesTypesEnum, string> = {
		[ServicesTypesEnum.HEALTH]: healthTitle,
		[ServicesTypesEnum.TELEHEALTH]: telehealthTitle,
		[ServicesTypesEnum.EXAMS]: examTitle,
		[ServicesTypesEnum.ODONTO]: odontoTitle,
		[ServicesTypesEnum.EMERGENCY_TELEHEALTH]: emergencyTitle,
		[ServicesTypesEnum.AUTHORIZATIONS]: authorizationsTitle,
		[ServicesTypesEnum.OTHERS]: '',
	};

	return (
		<Fragment>
			<MainHeader
				title={headerTitle[state.type]}
				handleBack={handleBack}
				variant="subRoute"
				hideClose
			/>
			<SubheaderWithSearch title={subtitle} />
			<StyledContainer>
				<StyledCard elevation={0}>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(handleSubmitPass)}>
							<Stack gap={2}>
								<StyledLabel htmlFor="operationalPass">{passLabel}</StyledLabel>
								<PasswordInput
									slotProps={{htmlInput: {maxLength: 6}}}
									label={inputLabel}
									hideHelperText
									name="operationalPass"
									fullWidth
								/>
								{IS_ERROR_PASS && (
									<AlertInfo.Root severity="error">
										<AlertInfo.Description>
											<Typography>{passwordErrorMessage}</Typography>
										</AlertInfo.Description>
									</AlertInfo.Root>
								)}
								<AlertInfo.Root>
									<AlertInfo.Title title={dontHavePass} />
									<AlertInfo.Description>
										<Typography>{description}</Typography>
										<AlertInfo.Action
											$iconName="arrow-right-filled"
											onClick={callChatRoom}
											disabled={callChatRoomIsLoading}
											endIcon={
												callChatRoomIsLoading ? (
													<Loading size={16} color="inherit" />
												) : null
											}>
											{secondaryButton}
										</AlertInfo.Action>
									</AlertInfo.Description>
								</AlertInfo.Root>
								<CTAButtonFixed.Root sx={{alignSelf: 'end'}}>
									<CTAButtonFixed.Action
										disabled={IS_DISABLED}
										type="submit"
										endIcon={
											IS_SUBMITTING ? (
												<Loading size={16} color="inherit" />
											) : (
												<Icon name="arrow-right-filled" />
											)
										}>
										{primaryButton}
									</CTAButtonFixed.Action>
								</CTAButtonFixed.Root>
							</Stack>
						</form>
					</FormProvider>
					<StyledImage>
						<img src={operationalCostImg} alt="" />
					</StyledImage>
				</StyledCard>
			</StyledContainer>

			<AlertModal
				open={IS_ERROR_GET_URL}
				onClose={() => setOperationStateUrl(OperationStateTypesEnum.NONE)}
				variant="info"
				title={serverErrorTitle}
				headerTitle={unexpectedError}
				description={serverError}
				buttonLabel={tryAgain}
			/>

			<SimpleConfirmationDrawer
				isOpened={openConfirmationDrawer}
				handleClose={handleCloseConfirmationDrawer}
				handleOpen={handleOpenConfirmationDrawer}
				handleConfirmAndContinue={() => {
					navigate(HealthBookingEnum.HEALTH_BOOKING_FLOW);
				}}
			/>
		</Fragment>
	);
}
